/**
 * Client-side bundle price preview + selection validation.
 *
 * IMPORTANT: This module is used for UX only. The AUTHORITATIVE price is
 * computed by the backend in `price_order_server_side` / `price_bundle_line`
 * — the customer builder mirrors that logic here to show a live total while
 * they configure the meal, but the total is discarded at checkout.
 *
 * Keeping the pricing rules in sync with the backend
 * ---------------------------------------------------
 * The two functions below use exactly the same fields as
 * `backend/server.py :: price_bundle_line`:
 *   pricing_mode = "included"       → 0
 *              = "fixed_surcharge" → +surcharge
 *              = "fixed_discount"  → -surcharge
 *              = "use_menu_price"  → +referenced menu item price
 *              = "above_allowance" → +max(0, menu_price - allowance)
 * Plus nested modifier group choices' `price` fields.
 */

/**
 * Get the extra cost added to the bundle base price for a single selection.
 *
 * @param {object} option        The bundle option config
 * @param {object} sourceProduct The referenced menu item (or null for custom / modifier group sources)
 */
export const optionUnitSurcharge = (option, sourceProduct = null) => {
  if (!option) return 0;
  const mode = option.pricing_mode || 'included';
  const surcharge = Number(option.surcharge || 0);
  const allowance = Number(option.allowance || 0);
  const menuPrice = Number(sourceProduct?.price || 0);
  switch (mode) {
    case 'fixed_surcharge':
      return surcharge;
    case 'fixed_discount':
      return -surcharge;
    case 'use_menu_price':
      return menuPrice;
    case 'above_allowance':
      return Math.max(0, menuPrice - allowance);
    case 'included':
    default:
      return 0;
  }
};

/**
 * Sum every nested modifier group `price` currently chosen for a selection.
 */
export const nestedCustomizationsTotal = (nested = {}) => {
  let total = 0;
  Object.values(nested).forEach((choices) => {
    const arr = Array.isArray(choices) ? choices : [choices];
    arr.forEach((c) => {
      if (c && typeof c === 'object' && c.price) total += Number(c.price) || 0;
    });
  });
  return total;
};

/**
 * Compute the unit price of a bundle line given the current selections.
 *
 * @param {object}   bundle              The bundle menu_item
 * @param {Array}    selections          Array of resolved selections
 * @param {Function} lookupProduct       (id) => sourceProduct (from cache)
 */
export const computeBundleUnitPrice = (bundle, selections, lookupProduct) => {
  const base = Number(bundle?.price || 0);
  let extras = 0;
  const groups = bundle?.bundle_config?.groups || [];
  const optionIndex = {};
  groups.forEach((g) => (g.options || []).forEach((o) => (optionIndex[o.id] = o)));
  (selections || []).forEach((sel) => {
    const opt = optionIndex[sel.option_id];
    if (!opt) return;
    const source = opt.source_ref ? lookupProduct(opt.source_ref) : null;
    extras += optionUnitSurcharge(opt, source);
    extras += nestedCustomizationsTotal(sel.nested_customizations);
  });
  return Math.max(0, Math.round((base + extras) * 100) / 100);
};

/**
 * Validate the current selection set. Returns { valid, missing[], errors[] }.
 * Mirrors the required/min/max/repeat-per-person rules the backend enforces.
 */
export const validateBundleSelections = (bundle, selections) => {
  const missing = [];
  const errors = [];
  const groups = bundle?.bundle_config?.groups || [];
  const byGroup = {};
  (selections || []).forEach((s) => {
    byGroup[s.group_id] = byGroup[s.group_id] || [];
    byGroup[s.group_id].push(s);
  });
  groups.forEach((grp) => {
    const gid = grp.id;
    const picks = byGroup[gid] || [];
    const repeat = !!grp.repeat_per_person;
    const personCount = repeat ? Math.max(1, Number(grp.person_count || 1)) : 1;
    const minPer = Number(
      grp.min_selections != null ? grp.min_selections : grp.required ? 1 : 0,
    );
    const maxPer = Number(grp.max_selections || minPer || 1);
    const requiredTotal = grp.required ? minPer * personCount : 0;

    if (repeat) {
      // Enforce per-person count for repeated groups.
      const bySlot = {};
      picks.forEach((p) => {
        const k = p.person_label || '';
        bySlot[k] = (bySlot[k] || 0) + 1;
      });
      (grp.person_labels || []).slice(0, personCount).forEach((label, idx) => {
        const key = label || `Person ${idx + 1}`;
        const c = bySlot[key] || 0;
        if (grp.required && c < minPer) {
          missing.push({ groupId: gid, label: `${grp.title || 'Selection'} — ${key}` });
        }
        if (c > maxPer) {
          errors.push(
            `${grp.title || 'Selection'} — ${key}: pick at most ${maxPer}.`,
          );
        }
      });
      // Mirror the backend cross-person duplicate rule so the customer
      // sees the problem BEFORE checkout blows up. Only applies when the
      // group forbids duplicates AND is single-choice.
      if (!grp.allow_duplicates && grp.selection_type !== 'multiple') {
        const optionUsage = {};
        picks.forEach((p) => {
          const key = p.option_id;
          optionUsage[key] = optionUsage[key] || new Set();
          optionUsage[key].add(p.person_label || '');
        });
        Object.values(optionUsage).forEach((slots) => {
          if (slots.size > 1) {
            errors.push(
              `${grp.title || 'Selection'}: each person must pick a different option.`,
            );
          }
        });
      }
    } else {
      if (grp.required && picks.length < requiredTotal) {
        missing.push({ groupId: gid, label: grp.title || 'Selection' });
      }
      if (picks.length > maxPer * personCount) {
        errors.push(
          `${grp.title || 'Selection'}: pick at most ${maxPer * personCount}.`,
        );
      }
    }
  });
  return { valid: missing.length === 0 && errors.length === 0, missing, errors };
};
